// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAddBooking, TeamRow, HotelProperty } from './hooks/useEventTeamTracker';

interface Props {
  open: boolean;
  onClose: () => void;
  team: TeamRow;
  eventId: string;
  hotels: HotelProperty[];
  userId: string;
}

export const AddBookingModal: React.FC<Props> = ({ open, onClose, team, eventId, hotels, userId }) => {
  const addBooking = useAddBooking(eventId);

  const [hotelPropertyId, setHotelPropertyId] = useState('');
  const [hotelName, setHotelName] = useState('');
  const [confNumber, setConfNumber] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [rooms, setRooms] = useState('1');
  const [rate, setRate] = useState('');
  const [status, setStatus] = useState('confirmed');
  const [notes, setNotes] = useState('');

  const handleHotelSelect = (id: string) => {
    setHotelPropertyId(id);
    const hotel = hotels.find((h) => h.id === id);
    if (hotel) {
      setHotelName(hotel.hotel_name);
      if (hotel.group_rate_per_night) setRate(String(hotel.group_rate_per_night));
    }
  };

  const selectedHotel = hotels.find((h) => h.id === hotelPropertyId);

  const handleSubmit = async () => {
    if (!hotelName.trim()) {
      toast.error('Hotel name is required');
      return;
    }
    if (!confNumber.trim()) {
      toast.error('Confirmation number is required');
      return;
    }
    if (!checkIn || !checkOut) {
      toast.error('Check-in and check-out dates are required');
      return;
    }
    if (!rate || parseFloat(rate) <= 0) {
      toast.error('Rate per night is required');
      return;
    }

    // room_nights / gross_revenue / commission_earned / rebate_earned are
    // computed by a database trigger — only send the raw fields.
    try {
      await addBooking.mutateAsync({
        registration_id: team.registration_id,
        event_id: eventId,
        hotel_property_id: hotelPropertyId || null,
        hotel_name: hotelName.trim(),
        confirmation_number: confNumber.trim(),
        check_in_date: checkIn,
        check_out_date: checkOut,
        number_of_rooms: parseInt(rooms) || 1,
        rate_per_night: parseFloat(rate),
        status,
        notes: notes.trim(),
        commission_pct: selectedHotel?.commission_pct ?? null,
        rebate_per_room: selectedHotel?.rebate_per_room ?? null,
        entered_by: userId,
        coach_email: team.coach_email,
        team_name: team.team_name,
      });
      toast.success('Booking added');
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to add booking');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Booking — {team.team_name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {hotels.length > 0 && (
            <div className="space-y-1.5">
              <Label>Select from contracted hotels</Label>
              <Select value={hotelPropertyId} onValueChange={handleHotelSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a hotel..." />
                </SelectTrigger>
                <SelectContent>
                  {hotels.map((h) => (
                    <SelectItem key={h.id} value={h.id}>{h.hotel_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Hotel Name *</Label>
            <Input value={hotelName} onChange={(e) => setHotelName(e.target.value)} placeholder="Hotel name" />
          </div>

          <div className="space-y-1.5">
            <Label>Confirmation # *</Label>
            <Input value={confNumber} onChange={(e) => setConfNumber(e.target.value)} placeholder="Confirmation number" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Check-in *</Label>
              <Input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Check-out *</Label>
              <Input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Rooms</Label>
              <Input type="number" min="1" value={rooms} onChange={(e) => setRooms(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Rate/Night *</Label>
              <Input type="number" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="0.00" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes..." rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={addBooking.isPending}>
            {addBooking.isPending ? 'Saving...' : 'Save Booking'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
